import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { ANY_AUTHENTICATED, normalizeRole } from "../../rbac/access";
import AIChatPanel from "./AIChatPanel";

/**
 * AILauncher — global floating button + panel container.
 *
 * Mount once at the app root. Listens for window event "brixi-ai:open" so any
 * button anywhere (existing "Ask Brixigo AI" CTAs) can trigger the panel without
 * threading props through the whole component tree.
 *
 * Usage from anywhere:
 *   window.dispatchEvent(new CustomEvent("brixi-ai:open", { detail: { prompt: "..." } }));
 */
export default function AILauncher() {
  const [open, setOpen] = useState(false);
  const { role, user, loading } = useContext(AuthContext) || {};
  const normalizedRole = normalizeRole(role);
  const isSignedIn = Boolean(user) && !loading;
  const hasAIAccess = isSignedIn && ANY_AUTHENTICATED.includes(normalizedRole);

  // Only show the Brixigo AI launcher on the Dashboard page.
  const location = useLocation();
  const onDashboard = location.pathname === "/dashboard";

  // Cross-component open trigger
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const prompt = e?.detail?.prompt;
      if (prompt) {
        // Defer one tick to let the panel mount and useAIChat init.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("brixi-ai:send", { detail: { prompt } }));
        }, 80);
      }
    };
    window.addEventListener("brixi-ai:open", handler);
    return () => window.removeEventListener("brixi-ai:open", handler);
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + J
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === "j" || e.key === "J")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Don't render anything for signed-out users or roles without AI access.
  if (!hasAIAccess) return null;
  // Restrict the floating launcher to the Dashboard only.
  if (!onDashboard) return null;

  const fabLabel =
    normalizedRole === "employee" ? "Ask Brixigo" :
    normalizedRole === "manager"  ? "Brixigo · Team" :
    normalizedRole === "hr"       ? "Brixigo · HR" :
    normalizedRole === "finance"  ? "Brixigo · Payroll" :
                                    "Brixigo AI";

  return (
    <>
      <AnimatePresence>
        {!open ? (
          <motion.button
            type="button"
            className="ai-fab"
            onClick={() => setOpen(true)}
            title={`${fabLabel} (Ctrl+J)`}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="ai-fab-glow" aria-hidden />
            <Sparkles size={18} />
            <span className="ai-fab-label">{fabLabel}</span>
          </motion.button>
        ) : null}
      </AnimatePresence>

      <AIChatPanel open={open} onClose={close} />
    </>
  );
}
